import * as React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFnsJalali } from '@mui/x-date-pickers/AdapterDateFnsJalali';
import {
  Dialog,
  DialogContent,
  Stack,
  TextField,
  Autocomplete,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Typography,
  Chip,
  DialogTitle,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ResponsiveDialogActions from './ResponsiveDialogAction';

const getMidnightDate = date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getEndOfDayDate = date => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

// --- Pixel Perfect Input Styles ---
const modernInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#F8FAFC', // Light subtle background
    minHeight: '52px',
    transition: 'all 0.2s ease-in-out',
    '& fieldset': {
      borderColor: '#E2E8F0', // Clean default border
      borderWidth: '1px',
    },
    '&:hover': {
      backgroundColor: '#F1F5F9',
    },
    '&:hover fieldset': {
      borderColor: '#CBD5E1',
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.12)', // Modern focus ring
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3B82F6',
      borderWidth: '1px',
    },
  },
  // Ensure the label color transitions smoothly
  '& .MuiInputLabel-root': {
    color: '#64748B',
    fontWeight: 500,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#3B82F6',
  },
  // Fixes for autocomplete inner paddings
  '& .MuiAutocomplete-inputRoot': {
    padding: '6px 12px !important',
    gap: '6px',
  },
};

// --- اصلاح استایل دکمه‌های بعد/قبل تقویم ---
const datePickerPopperSx = {
  direction: 'rtl',
  '& .MuiPickersCalendarHeader-labelContainer': {
    marginRight: 0,
    marginLeft: 'auto', // هدایت دکمه‌ها به سمت چپ تقویم
  },
  '& .MuiPickersArrowSwitcher-root': {
    direction: 'rtl',
  },
  '& .MuiPickersArrowSwitcher-root .MuiIconButton-root': {
    transform: 'rotate(180deg)', // چرخش فلش‌ها برای مطابقت با تقویم راست‌چین
  },
  '& .MuiPickersLayout-root': {
    direction: 'rtl',
  },
};

export default function PdfExportDialog({
  open,
  onClose,
  onConfirm,
  fetchTransactions,
}) {
  const [tabIndex, setTabIndex] = React.useState(0);

  // 1. Set default dates to the current day
  const [fromDate, setFromDate] = React.useState(new Date());
  const [toDate, setToDate] = React.useState(new Date());

  const [selectedTransactions, setSelectedTransactions] = React.useState([]);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    if (!inputValue || !/^\d+$/.test(inputValue)) {
      setOptions([]);
      return undefined;
    }

    setLoading(true);

    const delayDebounceFn = setTimeout(async () => {
      if (fetchTransactions) {
        try {
          const results = await fetchTransactions(inputValue);
          if (active) {
            setOptions(results || []);
          }
        } catch (error) {
          console.error('Error fetching transactions:', error);
          if (active) setOptions([]);
        }
      }
      if (active) setLoading(false);
    }, 500);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [inputValue, fetchTransactions]);

  const handleConfirm = () => {
    const queryParams = {};

    if (tabIndex === 0) {
      if (fromDate) queryParams.from = getMidnightDate(fromDate).toISOString();
      if (toDate) queryParams.to = getEndOfDayDate(toDate).toISOString();
    } else {
      const ids = selectedTransactions
        .map(t => {
          if (typeof t === 'string') return Number(t);
          return Number(t.id || t);
        })
        .filter(id => !isNaN(id));

      if (ids.length > 0) queryParams.ids = ids;
    }

    onConfirm(queryParams);
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle
        sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a' }}
      >
        فاکتور‌های فروش
      </DialogTitle>{' '}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label="محدوده زمانی"
            sx={{ fontWeight: 600, fontSize: '1rem', py: 2 }}
          />
          <Tab
            label="شناسه تراکنش‌ها"
            sx={{ fontWeight: 600, fontSize: '1rem', py: 2 }}
          />
        </Tabs>
      </Box>
      <LocalizationProvider dateAdapter={AdapterDateFnsJalali}>
        <DialogContent sx={{ minHeight: '260px', p: 3 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {tabIndex === 0 && (
              <>
                <DatePicker
                  label="از تاریخ"
                  value={fromDate}
                  onChange={newValue => setFromDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'medium',
                      sx: modernInputSx,
                    },
                    popper: { sx: datePickerPopperSx },
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      size="medium"
                      sx={modernInputSx}
                    />
                  )}
                />
                <DatePicker
                  label="تا تاریخ"
                  value={toDate}
                  onChange={newValue => setToDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'medium',
                      sx: modernInputSx,
                    },
                    // اضافه شدن استایل پاپ‌آپ
                    popper: { sx: datePickerPopperSx },
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      size="medium"
                      sx={modernInputSx}
                    />
                  )}
                />
              </>
            )}

            {tabIndex === 1 && (
              <Autocomplete
                multiple
                freeSolo
                dir="rtl"
                filterSelectedOptions
                filterOptions={x => x}
                options={options}
                value={selectedTransactions}
                onChange={(event, newValue) => {
                  const uniqueSelections = newValue.reduce((acc, current) => {
                    const currentId =
                      typeof current === 'string'
                        ? String(current)
                        : String(current.id || current);

                    const exists = acc.find(item => {
                      const itemId =
                        typeof item === 'string'
                          ? String(item)
                          : String(item.id || item);
                      return itemId === currentId;
                    });

                    if (!exists) {
                      acc.push(current);
                    }
                    return acc;
                  }, []);

                  setSelectedTransactions(uniqueSelections);
                  setInputValue('');
                }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                getOptionLabel={option => {
                  if (typeof option === 'string') return option;
                  return String(option.id || option);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const isString = typeof option === 'string';
                    const idStr = isString
                      ? option
                      : String(option.id || option);
                    const nameStr =
                      !isString && option.partyName ? option.partyName : '';

                    return (
                      <Chip
                        {...getTagProps({ index })}
                        key={idStr}
                        label={nameStr ? `#${idStr} (${nameStr})` : `#${idStr}`}
                        deleteIcon={<CloseIcon style={{ fontSize: 16 }} />}
                        sx={{
                          borderRadius: '8px',
                          backgroundColor: '#EFF6FF',
                          color: '#1D4ED8',
                          fontWeight: 600,
                          border: '1px solid #BFDBFE',
                          height: '32px', // Slightly taller
                          m: '2px', // Proper spacing between chips
                          boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                          '& .MuiChip-deleteIcon': {
                            color: '#93C5FD',
                            transition: 'color 0.2s',
                            '&:hover': { color: '#1E3A8A' },
                          },
                          '& .MuiChip-label': {
                            px: 1.5,
                          },
                        }}
                      />
                    );
                  })
                }
                renderOption={(props, option) => {
                  const idStr = String(option.id || option);
                  return (
                    <li {...props} key={idStr}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          width: '100%',
                          py: 0.5,
                          borderBottom: '1px solid #F1F5F9',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: '#3B82F6' }}
                        >
                          #{idStr}
                        </Typography>
                        {option.partyName && (
                          <Typography
                            variant="body2"
                            sx={{ color: '#475569', fontSize: '0.85rem' }}
                          >
                            {option.partyName}
                          </Typography>
                        )}
                      </Stack>
                    </li>
                  );
                }}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: '12px',
                      mt: 1,
                      boxShadow:
                        '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #E2E8F0',
                      '& .MuiAutocomplete-listbox': {
                        p: 1,
                        '& .MuiAutocomplete-option': {
                          borderRadius: '8px',
                          mb: 0.5,
                          '&:hover': {
                            backgroundColor: '#F8FAFC',
                          },
                          '&[aria-selected="true"]': {
                            backgroundColor: '#EFF6FF',
                          },
                        },
                      },
                    },
                  },
                }}
                loading={loading}
                loadingText={
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 1 }}
                  >
                    در حال بررسی...
                  </Typography>
                }
                noOptionsText={
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 1 }}
                  >
                    تراکنشی با این شناسه یافت نشد
                  </Typography>
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="شماره دقیق تراکنش"
                    placeholder={
                      selectedTransactions.length === 0
                        ? 'شماره فاکتور را وارد کنید...'
                        : ''
                    }
                    sx={modernInputSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? (
                            <CircularProgress
                              color="primary"
                              size={20}
                              sx={{ mr: 1 }}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Stack>

          <ResponsiveDialogActions
            onAccept={handleConfirm}
            onReject={onClose}
            acceptText={'دریافت فاکتور'}
            acceptButtonProps={{
              sx: {
                mt: 4,
                py: 1.2,
                px: 3,
                borderRadius: '10px',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.95rem',
                letterSpacing: '0.5px',
                background: 'linear-gradient(45deg, #3B82F6, #2563EB)',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #2563EB, #1D4ED8)',
                  boxShadow: '0 6px 16px rgba(37, 99, 235, 0.3)',
                  transform: 'translateY(-1px)',
                },
              },
            }}
            rejectButtonProps={{
              sx: {
                mt: 4,
                py: 1.2,
                px: 3,
                borderRadius: '10px',
                color: '#64748B',
                fontWeight: 600,
                fontSize: '0.95rem',
                backgroundColor: '#F1F5F9',
                border: 'none',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: '#FEE2E2',
                  color: '#E11D48',
                },
              },
            }}
          />
        </DialogContent>
      </LocalizationProvider>
    </Dialog>
  );
}
